import { useState, useEffect } from 'react';
import { Modal, Stack, Text, Group, Button, CopyButton, Divider, Loader, Box } from '@mantine/core';
import { Check, Copy, AlertCircle } from 'react-feather';
import { env } from '../../config/env';

interface AboutModalProps {
  opened: boolean;
  onClose: () => void;
}

interface WorkerStatus {
  service: string;
  version: string;
  build: string | null;
  commit: string;
  environment: string;
  status: 'healthy' | 'unreachable' | 'error';
  error?: string;
}

interface ApiHealth {
  api: {
    status: string;
    version: string;
    commit: string;
    build: string | null;
    environment: string;
  };
  workers: WorkerStatus[];
  timestamp: string;
}

/**
 * AboutModal - Displays version information for bug reporting
 * Shows app version, build number, commit hash, environment, and API version info
 */
export function AboutModal({ opened, onClose }: AboutModalProps) {
  const [apiHealth, setApiHealth] = useState<ApiHealth | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Fetch API health info when modal opens
  useEffect(() => {
    if (opened) {
      setLoading(true);
      setApiError(null);

      fetch(`${env.apiUrl}/api/health/full`)
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setApiHealth(data.data);
          } else {
            setApiError('Failed to fetch API version');
          }
        })
        .catch(() => {
          setApiError('Unable to connect to API');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [opened]);

  // Format version info for clipboard
  const formatVersionInfo = (): string => {
    const lines = [
      '--- Medley Version Info ---',
      `App Version: ${env.appVersion}`,
      env.buildNumber ? `Build: ${env.buildNumber}` : null,
      env.commitId ? `Commit: ${env.commitId}` : null,
      `Environment: ${env.environment}`,
      '',
      '--- API ---',
      apiHealth?.api ? `API Version: ${apiHealth.api.version}` : null,
      apiHealth?.api?.build ? `API Build: ${apiHealth.api.build}` : null,
      apiHealth?.api?.commit ? `API Commit: ${apiHealth.api.commit}` : null,
      apiHealth?.api?.environment ? `API Environment: ${apiHealth.api.environment}` : null,
      apiError ? `API Status: ${apiError}` : null,
    ].filter(Boolean);

    // Add worker info
    if (apiHealth?.workers && apiHealth.workers.length > 0) {
      lines.push('');
      lines.push('--- Workers ---');
      for (const worker of apiHealth.workers) {
        lines.push(`${worker.service}: ${worker.status === 'healthy' ? worker.version : worker.status}${worker.build ? `(${worker.build})` : ''}`);
      }
    }

    lines.push('');
    lines.push(`Generated: ${new Date().toISOString()}`);

    return lines.join('\n');
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="About Medley"
      size="sm"
      centered
    >
      <Stack gap="md">
        {/* App Version Section */}
        <Box>
          <Text size="sm" fw={600} c="dimmed" mb={4}>
            Application
          </Text>
          <Stack gap={4}>
            <Group justify="space-between">
              <Text size="sm">Version</Text>
              <Text size="sm" fw={500}>{env.appVersion}</Text>
            </Group>
            {env.buildNumber && (
              <Group justify="space-between">
                <Text size="sm">Build</Text>
                <Text size="sm" fw={500}>{env.buildNumber}</Text>
              </Group>
            )}
            {env.commitId && (
              <Group justify="space-between">
                <Text size="sm">Commit</Text>
                <Text size="sm" fw={500} ff="monospace">{env.commitId}</Text>
              </Group>
            )}
            <Group justify="space-between">
              <Text size="sm">Environment</Text>
              <Text size="sm" fw={500} tt="capitalize">{env.environment}</Text>
            </Group>
          </Stack>
        </Box>

        <Divider />

        {/* API Version Section */}
        <Box>
          <Text size="sm" fw={600} c="dimmed" mb={4}>
            API
          </Text>
          {loading ? (
            <Group justify="center" py="xs">
              <Loader size="sm" />
            </Group>
          ) : apiError ? (
            <Group gap="xs" c="red">
              <AlertCircle size={16} />
              <Text size="sm">{apiError}</Text>
            </Group>
          ) : apiHealth?.api ? (
            <Stack gap={4}>
              <Group justify="space-between">
                <Text size="sm">Version</Text>
                <Text size="sm" fw={500}>{apiHealth.api.version}</Text>
              </Group>
              {apiHealth.api.build && (
                <Group justify="space-between">
                  <Text size="sm">Build</Text>
                  <Text size="sm" fw={500}>{apiHealth.api.build}</Text>
                </Group>
              )}
              {apiHealth.api.commit && apiHealth.api.commit !== 'dev' && (
                <Group justify="space-between">
                  <Text size="sm">Commit</Text>
                  <Text size="sm" fw={500} ff="monospace">{apiHealth.api.commit}</Text>
                </Group>
              )}
              <Group justify="space-between">
                <Text size="sm">Environment</Text>
                <Text size="sm" fw={500} tt="capitalize">{apiHealth.api.environment}</Text>
              </Group>
            </Stack>
          ) : null}
        </Box>

        {/* Workers Section */}
        {apiHealth?.workers && apiHealth.workers.length > 0 && (
          <>
            {apiHealth.workers.map((worker) => (
              <Box key={worker.service}>
                <Divider />
                <Box>
                  <Text size="sm" fw={600} c="dimmed" mb={4} tt="capitalize">
                    {worker.service}
                  </Text>
                  {worker.status === 'healthy' ? (
                    <Stack gap={4}>
                      <Group justify="space-between">
                        <Text size="sm">Version</Text>
                        <Text size="sm" fw={500}>{worker.version}</Text>
                      </Group>
                      {worker.build && (
                        <Group justify="space-between">
                          <Text size="sm">Build</Text>
                          <Text size="sm" fw={500}>{worker.build}</Text>
                        </Group>
                      )}
                      {worker.commit && worker.commit !== 'dev' && worker.commit !== 'unknown' && (
                        <Group justify="space-between">
                          <Text size="sm">Commit</Text>
                          <Text size="sm" fw={500} ff="monospace">{worker.commit.substring(0, 7)}</Text>
                        </Group>
                      )}
                      {worker.environment && worker.environment !== 'unknown' && (
                        <Group justify="space-between">
                          <Text size="sm">Environment</Text>
                          <Text size="sm" fw={500} tt="capitalize">{worker.environment}</Text>
                        </Group>
                      )}
                    </Stack>
                  ) : (
                    <Group gap="xs" c="red">
                      <AlertCircle size={16} />
                      <Text size="sm">{worker.status}{worker.error ? `: ${worker.error}` : ''}</Text>
                    </Group>
                  )}
                </Box>
              </Box>
            ))}
          </>
        )}

        <Divider />

        {/* Copy Button */}
        <CopyButton value={formatVersionInfo()} timeout={2000}>
          {({ copied, copy }) => (
            <Button
              variant="light"
              fullWidth
              leftSection={copied ? <Check size={16} /> : <Copy size={16} />}
              color={copied ? 'green' : 'gray'}
              onClick={copy}
            >
              {copied ? 'Copied to Clipboard' : 'Copy Version Info'}
            </Button>
          )}
        </CopyButton>

        <Text size="xs" c="dimmed" ta="center">
          Include this info when reporting bugs
        </Text>
      </Stack>
    </Modal>
  );
}
